/**
 * JWT Token utility functions
 */

interface JwtPayload {
  exp: number;
  sub: string;
  type: string;
  [key: string]: any;
}

/**
 * Decode a JWT token to extract the payload
 * @param token - JWT token string
 * @returns Decoded JWT payload or null if invalid
 */
export function decodeToken(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decodedPayload = atob(payload);
    return JSON.parse(decodedPayload) as JwtPayload;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @param bufferSeconds - Optional buffer time in seconds before actual expiry (default: 0)
 * @returns true if token is expired or invalid, false otherwise
 */
export function isTokenExpired(token: string, bufferSeconds: number = 0): boolean {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return true; // Invalid token or no expiration claim
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = bufferSeconds * 1000;

  return currentTime >= expirationTime - bufferTime;
}

/**
 * Get the expiration date of a JWT token
 * @param token - JWT token string
 * @returns Date object or null if token is invalid
 */
export function getTokenExpirationDate(token: string): Date | null {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return new Date(payload.exp * 1000);
}

/**
 * Get the time remaining until token expires (in milliseconds)
 * @param token - JWT token string
 * @returns Milliseconds until expiration or 0 if expired/invalid
 */
export function getTokenTimeRemaining(token: string): number {
  const expirationDate = getTokenExpirationDate(token);
  if (!expirationDate) {
    return 0;
  }

  const timeRemaining = expirationDate.getTime() - Date.now();
  console.log('Token time remaining (ms):', timeRemaining);
  return Math.max(0, timeRemaining);
}
